import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
//api
import { getAwardWinningBooks, deleteAwardWinningBook } from 'api/company';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import useDebounce from 'utils/customHooks/useDebounce';
//components
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import Title from 'components/backoffice/texts/title';
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Button from 'components/backoffice/buttonCustom';
import SearchTable from 'components/backoffice/table/SearchTable';

const AwardWinningBooks = () => {
  const navigate = useNavigate();
  const userPermissions = usePermissions();
  const [showModalDelete, setShowModalDelete] = useState(false);
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext();
  const [inputValue, setInputValue] = useState('');
  const debouncedSearchValue = useDebounce(inputValue, 500);
  const [books, setBooks] = useState([]);
  const [page, setPage] = useState(1);
  const [responseInfo, setResponseInfo] = useState({});
  const { loading } = useHandleSearch();
  const [idBook, setIdBook] = useState();

  const cols = ['ID', t('nombre'), t('productTitle'), t('year'), t('actions')]

  const getBooks = useCallback(async () => {
    try {
      const response = await getAwardWinningBooks({
        page,
        page_size: 10,
        search: debouncedSearchValue
      });

      if (response?.data?.results) {
        setBooks(response.data.results);
        setResponseInfo({
          totalPages: response.data.total_pages,
          noResults: response.data.results.length === 0
        });
      } else {
        setBooks([]);
        setResponseInfo({ noResults: true });
      }
    } catch (error) {
      toast.error(`${t('error')}: ${t('mensajeerror')}`);
    }
  }, [page, debouncedSearchValue, t]);

  const handleShowModalDeleteBook = (item, id, setOpenModal) => {
    if (item.id === id) {
      setOpenModal(true);
      setIdBook(id);
    }
  };

  const handleDelete = useCallback(async () => {
    try {
      const { status } = await deleteAwardWinningBook(idBook);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        getBooks();
      } else {
        toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
      }
    } catch (error) {
      console.error(error);
      toast.error(`${t('error')}: ${t('mensajeerror')}`);
    }
  }, [idBook, getBooks, t]);

  const handleClear = () => {
    setInputValue('');
    setPage(1);
    setResponseInfo(prev => ({ ...prev, noResults: false }));
  };

  useEffect(() => {
    setTitle(t('awardWinningBooks'))
    return () => {
      setTitle('');
    };
  }, [setTitle, t]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    getBooks();
  }, [getBooks]);

  const handleCreate = () => navigate('/intranet/award-winning-books/create');
  const handleEdit = (id) => navigate(`/intranet/award-winning-books/edit/${id}`);

  return (
    <div className="d-lg-grid">
      <Title text={t('awardWinningBooks')} />
      <Row className="mt-3">
        <Col md={5} xs={12} className="d-flex text-start">
          <SearchTable
            value={inputValue}
            handleSearch={setInputValue}
            handleClear={handleClear}
            placeholder={t('searchByName')}
          />
        </Col>
        <Col md={7} xs={12} className="text-end mt-3 mt-md-0">
          {userPermissions.includes('empresa.__add') && (
            <Button color="primary" onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={cols}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {books.map((book, index) => (
            <tr key={book.id}>
              <td>{book.id}</td>
              <td>{book.nombre}</td>
              <td>{book.products?.[0]?.title || t('N/A')}</td>
              <td>{book.products?.[0]?.year || t('N/A')}</td>
              <td className="item-dropdown text-end">
                <DropdownIcons
                  idItem={index}
                  handleEdit={
                    userPermissions.includes('empresa.__modify') &&
                    (() => handleEdit(book.id))
                  }
                  handleDelete={
                    userPermissions.includes('empresa.__delete') &&
                    (() => handleShowModalDeleteBook(book, book.id, setShowModalDelete))
                  }
                />
              </td>
            </tr>
          ))}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showModalDelete}
        setOpenModal={setShowModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deleteAwardWinningBook')}
        beforeTitle={t('awardWinningBooks')}
      />
    </div>
  );
};

export default AwardWinningBooks;